////// Lib. //////
//////////////////

import Component from "gia/Component";
import { gsap } from "gsap";
import WorkStreamViewer from "./work-stream-viewer.js";
import ImagePrefetcher from "../../../../app/modules/ImagePrefetcher.js";
import { createScopedLazyLoad } from "../../../../app/modules/LazyLoad.js";

///// Assets /////
//////////////////

const defaultLogStyles = {
  default: "#6682d6",
  action: "#c7d0ff",
  warning: "#ffaf00",
  error: "#ff3232",
};

const MOBILE_MEDIA_QUERY = "(max-width: 767px), (max-height: 767px) and (pointer: coarse)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const MOBILE_FRAME_INTERVAL = 1 / 30;

// Comp. class ///
//////////////////

export default class WorkStream extends Component {
  /////////// Constructor ////////////
  ////////////////////////////////////

  constructor(el, options) {
    super(el);

    //// DOM references ////
    ////////////////////////

    this.ref = {
      scene: null, // perspective container — separated from overflow-hidden to fix Safari 3D flattening
      layer: [],
      viewerRoot: null,
      viewerBackdrop: null,
      viewerFigure: null,
      viewerImgBase: null,
      viewerImgHi: null,
      viewerCaption: null,
      viewerCaptionBottom: null,
      viewerTitle: null,
      viewerLink: null,
      viewerClose: null,
    };

    // The bg-grid is rendered outside the component root, so gia's descendant-scoped
    // ref collection cannot reach it. Resolve it explicitly so mount()/ticker/unmount()
    // keep driving its animation (single full-screen WorkStream instance).
    const bgGrid = document.querySelector("[data-work-stream-bg-grid]");
    if (bgGrid) this.ref.layer.push(bgGrid);

    /////// Options ////////
    ////////////////////////

    this.options = {
      name: "WorkStream",
      speed: 60,
      mobilespeed: 24,
      depthz: 300,
      startdelay: 0,
      viewerspeedfactor: 0.5, // stream speed while the enlarged viewer is open
      viewerduration: 0.6,
      viewerblur: 12, // px of blur applied to the stream while the viewer is open
      prefetch: true,
      prefetchConcurrency: 2,
      logs: false,
      ...options,
    };

    /////// State //////////
    ////////////////////////

    this._layers = [];
    this._userVelocity = 0;
    this._speed = { factor: 1 };
    this._viewer = null;
    this._prefetcher = null;
    this._lazyLoad = null;
    this._onItemClick = null;
    this._tickerFn = null;
    this._onWheel = null;
    this._onTouchStart = null;
    this._onTouchMove = null;
    this._onVisibilityChange = null;
    this._lastTouchY = 0;
    this._isMobile = false;
    this._prefersReducedMotion = false;
    this._documentHidden = false;
    this._isViewerPaused = false;
    this._activeLayerElements = [];
    this._lastFrameTime = 0;
  }

  ///////////// Mount ////////////////
  ////////////////////////////////////

  mount() {
    if (this.options.logs) console.log("%c WorkStream.mount()", `color: ${defaultLogStyles.action}`);

    this._isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    this._prefersReducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    this._documentHidden = document.hidden;
    this.element.classList.toggle("work-stream--mobile", this._isMobile);

    this._activeLayerElements = this.ref.layer.filter((el) => {
      if (this._isMobile && el.classList.contains("work-stream__bg-grid")) return false;
      return !this._isMobile || !el.hasAttribute("data-ws-mobile-optional");
    });

    if (this._isMobile) {
      document.querySelector("[data-work-stream-bg-grid]")?.style.removeProperty("transform");
    }

    // Pre-apply transforms synchronously so there is no unstyled first frame.
    const perspective = parseFloat((this.ref.scene || this.element).style.perspective) || 1200;
    const introVh = this.options.startdelay > 0 ? window.innerHeight : 0;
    this._activeLayerElements.forEach((el) => {
      const z = parseInt(el.dataset.z ?? "0", 10);
      const scale = z < 0 ? (perspective - z) / perspective : 1;
      // Pre-apply intro offset so there is no first-frame flash.
      const yOff = introVh && el.classList.contains("work-stream__layer") ? introVh : 0;
      el.style.transform = this._isMobile ? `translateY(${yOff}px)` : `translateZ(${z}px) scale(${scale}) translateY(${yOff}px)`;
    });

    requestAnimationFrame(() => this._init());
  }

  //////////// Unmount ///////////////
  ////////////////////////////////////

  unmount() {
    if (this.options.logs) console.log("%c WorkStream.unmount()", `color: ${defaultLogStyles.action}`);

    this._viewer?.destroy();
    this._viewer = null;

    this._prefetcher?.destroy();
    this._prefetcher = null;
    this._lazyLoad = null;

    // It was reparented to body, so a Swup content swap would otherwise leave it orphaned.
    this.ref.viewerRoot?.remove();

    gsap.killTweensOf(this._speed);
    this._speed.factor = 1;
    this._isViewerPaused = false;

    if (this._onItemClick) {
      this.element.removeEventListener("click", this._onItemClick);
      this._onItemClick = null;
    }

    if (this._introTimeoutId) {
      clearTimeout(this._introTimeoutId);
      this._introTimeoutId = null;
    }
    this._layers
      .filter((l) => l.el.classList.contains("work-stream__layer"))
      .forEach((l) => {
        l.frozen = false;
      });

    this._layers.forEach((l) => {
      l.el.style.willChange = "";
    });

    if (this._tickerFn) {
      gsap.ticker.remove(this._tickerFn);
      this._tickerFn = null;
    }

    if (this._onWheel) window.removeEventListener("wheel", this._onWheel, { passive: true });
    if (this._onTouchStart) window.removeEventListener("touchstart", this._onTouchStart, { passive: true });
    if (this._onTouchMove) window.removeEventListener("touchmove", this._onTouchMove, { passive: true });
    if (this._onVisibilityChange) document.removeEventListener("visibilitychange", this._onVisibilityChange);

    this.element.classList.remove("work-stream--mobile");
  }

  /////////// Private ////////////////
  ////////////////////////////////////

  _initViewer() {
    if (!this.ref.viewerRoot) return;

    // The component root is position: fixed and so always forms a stacking context, which would
    // pin the viewer below the nav no matter its z-index. Reparenting to body lets it outrank them.
    document.body.appendChild(this.ref.viewerRoot);

    // The scene covers both image layers and the brand overlay; the bg-grid and the nav live
    // outside the component root and opt in via the attribute instead.
    const blurTargets = [this.ref.scene, ...document.querySelectorAll("[data-work-stream-blur]")].filter(Boolean);

    this._viewer = new WorkStreamViewer({
      refs: this.ref,
      duration: this.options.viewerduration,
      blurTargets,
      blur: this._isMobile ? 0 : this.options.viewerblur,
      getTwins: (box) => this._twinsFor(box),
      onOpen: () => {
        if (this._isMobile) {
          this._isViewerPaused = true;
          document.documentElement.classList.add("has-work-stream-viewer-open");
          return;
        }

        this._tweenSpeed(this.options.viewerspeedfactor);
      },
      onClose: () => {
        if (!this._isMobile) this._tweenSpeed(1);
      },
      onFinishClose: () => {
        this._isViewerPaused = false;
        document.documentElement.classList.remove("has-work-stream-viewer-open");
      },
    });

    this._onItemClick = (e) => {
      const button = e.target.closest("[data-ws-item]");
      if (button && this.element.contains(button)) this._viewer.open(button);
    };

    this.element.addEventListener("click", this._onItemClick);
  }

  // The loop cycles through every item eventually, so the whole grid is worth warming in the
  // background — DOM order approximates first-to-become-visible well enough to prioritise the queue.
  _startPrefetch() {
    if (!this.options.prefetch) return;

    const urls = Array.from(this.element.querySelectorAll("img[data-src]")).map((img) => img.dataset.src);

    this._prefetcher = new ImagePrefetcher({ concurrency: this.options.prefetchConcurrency });
    this._prefetcher.add(urls);
    this._prefetcher.start();
  }

  // Every set is rendered twice per layer for the loop, so an item exists as two nodes.
  // Both must be resolved: the viewer hides both while open and flies back to whichever
  // one is on screen at close time.
  _twinsFor(box) {
    const item = box.closest(".work-stream__item");
    const layer = box.closest(".work-stream__layer");
    if (!item || !layer) return [box];

    return Array.from(layer.querySelectorAll(`.work-stream__item[data-ws-index="${item.dataset.wsIndex}"] .work-stream__item-box`));
  }

  _tweenSpeed(factor) {
    gsap.to(this._speed, { factor, duration: this.options.viewerduration, ease: "power2.inOut", overwrite: true });
  }

  _init() {
    if (!this._activeLayerElements.length) {
      if (this.options.logs) console.warn("%c WorkStream._init(): no layer refs", `color: ${defaultLogStyles.warning}`);
      return;
    }

    // Z depth is declared per-layer via data-z attribute.
    // compensationScale keeps the layer visually full-screen despite perspective shrinkage.
    const perspective = parseFloat((this.ref.scene || this.element).style.perspective) || 1200;
    this._layers = this._activeLayerElements.map((el) => {
      const z = parseInt(el.dataset.z ?? "0", 10);
      const scale = this._isMobile ? 1 : z < 0 ? (perspective - z) / perspective : 1;
      const firstSet = el.querySelector(".work-stream__set");
      const setHeight = firstSet ? firstSet.offsetHeight : 0;
      el.style.willChange = "transform";
      return { el, z, scale, setHeight, y: 0, frozen: false, introComplete: false };
    });

    if (this._layers.some((l) => l.setHeight === 0)) {
      if (this.options.logs) console.warn("%c WorkStream._init(): a layer has setHeight 0, aborting", `color: ${defaultLogStyles.warning}`);
      return;
    }

    const isPaused = () => this.options.speed <= 0 || this._prefersReducedMotion || this._documentHidden || this._isViewerPaused;

    // Freeze the bg-grid on Safari — it's the biggest perf cost and perspective already helps less there.
    const isSafari = /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);
    if (isSafari && !this._isMobile) {
      this._layers
        .filter((l) => l.el.classList.contains("work-stream__bg-grid"))
        .forEach((l) => {
          l.frozen = true;
          l.introComplete = true;
        });
    }

    // ── Event handlers ───────────────────────────────────────────────────

    this._onWheel = (e) => {
      if (isPaused() || this._viewer?.isOpen) return;
      let delta = e.deltaY;
      if (e.deltaMode === 1) delta *= 20;
      if (e.deltaMode === 2) delta *= 400;
      this._userVelocity += delta * 0.5;
      this._userVelocity = Math.max(-300, Math.min(300, this._userVelocity));
    };

    this._onTouchStart = (e) => {
      this._lastTouchY = e.touches[0].clientY;
    };

    this._onTouchMove = (e) => {
      if (isPaused() || this._viewer?.isOpen) return;
      const delta = this._lastTouchY - e.touches[0].clientY;
      this._lastTouchY = e.touches[0].clientY;
      this._userVelocity += delta * (this._isMobile ? 0.8 : 1.2);
      const maxVelocity = this._isMobile ? 180 : 300;
      this._userVelocity = Math.max(-maxVelocity, Math.min(maxVelocity, this._userVelocity));
    };

    this._onVisibilityChange = () => {
      this._documentHidden = document.hidden;
      if (!this._documentHidden) this._lastFrameTime = 0;
    };

    window.addEventListener("wheel", this._onWheel, { passive: true });
    window.addEventListener("touchstart", this._onTouchStart, { passive: true });
    window.addEventListener("touchmove", this._onTouchMove, { passive: true });
    document.addEventListener("visibilitychange", this._onVisibilityChange);

    this._initViewer();
    this._lazyLoad = createScopedLazyLoad(this.element);
    this._startPrefetch();

    const imageLayers = this._layers.filter((l) => l.el.classList.contains("work-stream__layer"));
    if (this.options.startdelay > 0) {
      const vh = window.innerHeight;
      imageLayers.forEach((l) => {
        l.y = -vh;
        l.frozen = true;
        l.introComplete = false;
        l.el.style.transform = this._isMobile ? `translateY(${vh}px)` : `translateZ(${l.z}px) scale(${l.scale}) translateY(${vh}px)`;
      });
    }

    // ── GSAP ticker ───────────────────────────────────────────────────────────
    // Dividing the advance by each layer's compensationScale restores the natural
    // parallax rate: screen-space movement = advance × p/(p+|z|).

    this._tickerFn = (time, deltaTime) => {
      if (this._documentHidden || this._prefersReducedMotion) return;
      if (this._isMobile && this._lastFrameTime && time - this._lastFrameTime < MOBILE_FRAME_INTERVAL) return;

      const dt = this._isMobile && this._lastFrameTime ? Math.min((time - this._lastFrameTime) * 1000, 100) : Math.min(deltaTime, 100);
      this._lastFrameTime = time;

      const baseSpeed = this._isMobile ? this.options.mobilespeed : this.options.speed;
      const advance = isPaused() ? 0 : (baseSpeed * this._speed.factor * dt) / 1000 + this._userVelocity;

      if (isPaused()) {
        this._userVelocity = 0;
      } else {
        this._userVelocity *= this._isMobile ? 0.85 : 0.9;
        if (Math.abs(this._userVelocity) < 0.05) this._userVelocity = 0;
      }

      this._layers.forEach((layer) => {
        if (!layer.frozen) layer.y += advance / layer.scale;
        // Mark intro done once y first crosses 0; after that modulo handles all values including negative.
        if (!layer.introComplete && layer.y >= 0) layer.introComplete = true;
        const y = !layer.introComplete && layer.y < 0 ? layer.y : ((layer.y % layer.setHeight) + layer.setHeight) % layer.setHeight;
        layer.el.style.transform = this._isMobile ? `translateY(${-y}px)` : `translateZ(${layer.z}px) scale(${layer.scale}) translateY(${-y}px)`;
      });
    };

    gsap.ticker.add(this._tickerFn);

    // Reveal image layers now that they are safely positioned below the viewport.
    if (this.options.startdelay > 0) {
      imageLayers.forEach((l) => {
        l.el.style.opacity = "1";
      });
    }

    // Image layers start below the viewport (y = -vh) and are frozen until startdelay elapses.
    // Unfreezing lets the ticker advance y naturally — images enter from below at normal speed.
    if (this.options.startdelay > 0) {
      this._introTimeoutId = setTimeout(() => {
        this._introTimeoutId = null;
        imageLayers.forEach((l) => {
          l.frozen = false;
        });
      }, this.options.startdelay * 1000);
    }
  }
}
